import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { BlockLine } from '../types/blocks';
import type { QuestionHistory } from './questionSelection';
import { createInitialHistory, normalizeQuestionHistory } from './questionSelection';
import { getInitialSelectedGoal } from './achievements/achievementMetadata';
import { useExperimentSpeculativeData } from '../../../../hooks/useExperimentSpeculativeData';
import { useAppSelector } from '../../../../redux/hooks';
import { selectUser } from '../../../../redux/reducers/userSlice';
import { TEAME_183_DATA_PERSISTENCE_VARIANT, TEAME_183_FLAG_ID } from '../../constants';
import { BetaFeedbackRating, isBetaFeedbackRating } from './betaFeedback';

// Types for student progress and state
export type AchievementType = 'empty' | 'bronze' | 'silver' | 'gold';
export type LearningPage = 'explore' | 'connect' | 'practice' | 'challenge' | 'exceed';

export interface Achievement {
  id: string;
  state: AchievementType;
}

export interface ManipulativeState {
  lines: BlockLine[];
  arrangement: 'parallel' | 'sequence';
  cloneMultiplier: number;
}

export interface StudentProgress {
  currentActivity: string | null;
  completedActivities: string[];
  attemptCounts: Record<string, number>;
  startTime: number;
  lastActivityTime: number;
  achievements: Record<string, AchievementType>;
  resetKey: number;
  manipulativeResetKey: number; // Separate key for resetting just the manipulative
  advancedMode: boolean;
  currentPage: LearningPage;
  selectedGoal: string | null;
  manipulativeState: ManipulativeState;
  // Advanced Mode tracking
  advancedModeQuestionsCorrect: number;
  advancedModeFirstTryCorrect: number;
  advancedModeCurrentStreak: number;
  advancedModeMaxStreak: number;
  // Advanced Mode question history
  advancedModeQuestionHistory: QuestionHistory;
  betaFeedbackRating: BetaFeedbackRating | null;
  betaFeedbackText: string;
  connectCurrentSlideIndex: number;
  connectViewedSlideIds: string[];
  exceedRiceGamePoints?: Array<{ rice: number; water: number }>;
}

interface MathEducationContextType {
  progress: StudentProgress;
  updateProgress: (updates: Partial<StudentProgress>) => void;
  resetExperience: () => void;
  incrementAttempts: (activityId: string) => void;
  markActivityComplete: (activityId: string) => void;
  getElapsedTime: () => number;
  unlockAchievement: (achievementId: string, level?: AchievementType) => void;
  activateAdvancedMode: () => void;
  setCurrentPage: (page: LearningPage) => void;
  setSelectedGoal: (achievementId: string) => void;
  updateManipulativeState: (state: Partial<ManipulativeState>) => void;
  recordAdvancedModeAnswer: (isCorrect: boolean, isFirstTry: boolean) => void;
  updateQuestionHistory: (history: QuestionHistory) => void;
  unlockingBadges: Map<string, AchievementType>;
  onBadgeUnlockStart: (badgeId: string, level: AchievementType) => void;
  onBadgeUnlockComplete: (badgeId: string) => void;
}

const MathEducationContext = createContext<MathEducationContextType | null>(null);

const ACHIEVEMENT_LEVEL_RANK: Record<AchievementType, number> = {
  empty: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
};

const createInitialProgress = (): StudentProgress => {
  const achievements: Record<string, AchievementType> = {};
  return {
    currentActivity: null,
    completedActivities: [],
    attemptCounts: {},
    startTime: Date.now(),
    lastActivityTime: Date.now(),
    achievements,
    resetKey: Date.now(),
    manipulativeResetKey: Date.now(),
    advancedMode: false,
    currentPage: 'explore',
    selectedGoal: getInitialSelectedGoal(achievements),
    manipulativeState: {
      lines: [],
      arrangement: 'parallel',
      cloneMultiplier: 1,
    },
    advancedModeQuestionsCorrect: 0,
    advancedModeFirstTryCorrect: 0,
    advancedModeCurrentStreak: 0,
    advancedModeMaxStreak: 0,
    advancedModeQuestionHistory: createInitialHistory(),
    betaFeedbackRating: null,
    betaFeedbackText: '',
    connectCurrentSlideIndex: 0,
    connectViewedSlideIds: [],
    exceedRiceGamePoints: [],
  };
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

function isValidPersistedProgress(value: unknown): value is StudentProgress {
  if (!isObject(value)) {
    return false;
  }

  const candidate = value as Partial<StudentProgress>;
  if (
    !isObject(candidate.achievements) ||
    !isObject(candidate.attemptCounts) ||
    !Array.isArray(candidate.completedActivities)
  ) {
    return false;
  }

  if (
    !isObject(candidate.manipulativeState) ||
    !Array.isArray(candidate.manipulativeState.lines) ||
    (candidate.manipulativeState.arrangement !== 'parallel' &&
      candidate.manipulativeState.arrangement !== 'sequence') ||
    typeof candidate.manipulativeState.cloneMultiplier !== 'number'
  ) {
    return false;
  }

  return (
    (candidate.currentActivity === null || typeof candidate.currentActivity === 'string') &&
    typeof candidate.startTime === 'number' &&
    typeof candidate.lastActivityTime === 'number' &&
    typeof candidate.resetKey === 'number' &&
    typeof candidate.manipulativeResetKey === 'number' &&
    typeof candidate.advancedMode === 'boolean' &&
    (candidate.currentPage === undefined ||
      candidate.currentPage === 'explore' ||
      candidate.currentPage === 'connect' ||
      candidate.currentPage === 'practice' ||
      candidate.currentPage === 'challenge' ||
      candidate.currentPage === 'exceed') &&
    (candidate.selectedGoal === null || typeof candidate.selectedGoal === 'string') &&
    typeof candidate.advancedModeQuestionsCorrect === 'number' &&
    typeof candidate.advancedModeFirstTryCorrect === 'number' &&
    typeof candidate.advancedModeCurrentStreak === 'number' &&
    typeof candidate.advancedModeMaxStreak === 'number' &&
    isObject(candidate.advancedModeQuestionHistory) &&
    (candidate.betaFeedbackRating === undefined ||
      candidate.betaFeedbackRating === null ||
      isBetaFeedbackRating(candidate.betaFeedbackRating)) &&
    (candidate.betaFeedbackText === undefined || typeof candidate.betaFeedbackText === 'string') &&
    (candidate.connectCurrentSlideIndex === undefined || typeof candidate.connectCurrentSlideIndex === 'number') &&
    (candidate.connectViewedSlideIds === undefined ||
      (Array.isArray(candidate.connectViewedSlideIds) &&
        candidate.connectViewedSlideIds.every(id => typeof id === 'string'))) &&
    (candidate.exceedRiceGamePoints === undefined ||
      (Array.isArray(candidate.exceedRiceGamePoints) &&
        candidate.exceedRiceGamePoints.every(
          p => isObject(p) && typeof p.rice === 'number' && typeof p.water === 'number',
        )))
  );
}

function mergePersistedProgressWithDefaults(defaults: StudentProgress, persisted: StudentProgress): StudentProgress {
  const mergedCurrentPage = persisted.currentPage ?? (persisted.advancedMode ? 'practice' : 'explore');
  return {
    ...defaults,
    ...persisted,
    attemptCounts: {
      ...defaults.attemptCounts,
      ...persisted.attemptCounts,
    },
    achievements: {
      ...defaults.achievements,
      ...persisted.achievements,
    },
    manipulativeState: {
      ...defaults.manipulativeState,
      ...persisted.manipulativeState,
      lines: Array.isArray(persisted.manipulativeState?.lines)
        ? persisted.manipulativeState.lines
        : defaults.manipulativeState.lines,
    },
    completedActivities: Array.isArray(persisted.completedActivities)
      ? persisted.completedActivities
      : defaults.completedActivities,
    advancedModeQuestionHistory: isObject(persisted.advancedModeQuestionHistory)
      ? normalizeQuestionHistory(persisted.advancedModeQuestionHistory, defaults.advancedModeQuestionHistory)
      : defaults.advancedModeQuestionHistory,
    currentPage: mergedCurrentPage,
    advancedMode: mergedCurrentPage === 'practice',
    connectCurrentSlideIndex:
      typeof persisted.connectCurrentSlideIndex === 'number' && persisted.connectCurrentSlideIndex >= 0
        ? Math.floor(persisted.connectCurrentSlideIndex)
        : defaults.connectCurrentSlideIndex,
    connectViewedSlideIds: Array.isArray(persisted.connectViewedSlideIds)
      ? persisted.connectViewedSlideIds.filter(id => typeof id === 'string')
      : defaults.connectViewedSlideIds,
    exceedRiceGamePoints: Array.isArray(persisted.exceedRiceGamePoints)
      ? persisted.exceedRiceGamePoints.filter(
          p => isObject(p) && typeof p.rice === 'number' && typeof p.water === 'number',
        )
      : defaults.exceedRiceGamePoints,
  };
}

export function MathEducationProvider({ children }: { children: React.ReactNode }) {
  const userRedux = useAppSelector(selectUser);
  const defaultProgress = useMemo(() => createInitialProgress(), []);

  const { data: progress, setData: setProgress } = useExperimentSpeculativeData<StudentProgress>({
    publicUserId: userRedux?.sub,
    userNodeId: userRedux?.id ? `UserNode:${userRedux.id}` : undefined,
    flagName: TEAME_183_FLAG_ID,
    variantName: TEAME_183_DATA_PERSISTENCE_VARIANT,
    defaultValues: defaultProgress,
    validate: isValidPersistedProgress,
    merge: mergePersistedProgressWithDefaults,
  });

  // Track badges currently animating their unlock (separate from achievement state)
  // Map of badgeId -> achievement level (gold/silver/bronze)
  const [unlockingBadges, setUnlockingBadges] = useState<Map<string, AchievementType>>(new Map());
  const achievementsRef = useRef(progress.achievements);
  const unlockingBadgesRef = useRef(unlockingBadges);

  useEffect(() => {
    achievementsRef.current = progress.achievements;
  }, [progress.achievements]);

  useEffect(() => {
    unlockingBadgesRef.current = unlockingBadges;
  }, [unlockingBadges]);

  const updateProgress = useCallback((updates: Partial<StudentProgress>) => {
    setProgress(prev => ({
      ...prev,
      ...updates,
      lastActivityTime: Date.now(),
    }));
  }, []);

  const resetExperience = useCallback(() => {
    setProgress(createInitialProgress());
  }, []);

  const incrementAttempts = useCallback((activityId: string) => {
    setProgress(prev => ({
      ...prev,
      attemptCounts: {
        ...prev.attemptCounts,
        [activityId]: (prev.attemptCounts[activityId] || 0) + 1,
      },
      lastActivityTime: Date.now(),
    }));
  }, []);

  const markActivityComplete = useCallback((activityId: string) => {
    setProgress(prev => ({
      ...prev,
      completedActivities: prev.completedActivities.includes(activityId)
        ? prev.completedActivities
        : [...prev.completedActivities, activityId],
      lastActivityTime: Date.now(),
    }));
  }, []);

  const getElapsedTime = useCallback(() => {
    return Date.now() - progress.startTime;
  }, [progress.startTime]);

  // Badge unlock animation state management.
  const onBadgeUnlockStart = useCallback((badgeId: string, level: AchievementType) => {
    setUnlockingBadges(prev => {
      const currentLevel = prev.get(badgeId);
      if (currentLevel && ACHIEVEMENT_LEVEL_RANK[currentLevel] >= ACHIEVEMENT_LEVEL_RANK[level]) {
        return prev;
      }

      const next = new Map(prev);
      next.set(badgeId, level);
      return next;
    });
  }, []);

  const onBadgeUnlockComplete = useCallback((badgeId: string) => {
    // Get the level from unlocking badges before removing
    setUnlockingBadges(prev => {
      const level = prev.get(badgeId);

      // NOW update core badge state (after animation completes)
      if (level) {
        setProgress(current => ({
          ...current,
          achievements: {
            ...current.achievements,
            [badgeId]: level,
          },
          lastActivityTime: Date.now(),
        }));
      }

      // Remove from unlocking set
      const newMap = new Map(prev);
      newMap.delete(badgeId);
      return newMap;
    });
  }, []);

  const unlockAchievement = useCallback(
    (achievementId: string, level: AchievementType = 'gold') => {
      // Guard against repeated unlock triggers:
      // 1) badge already committed at this level or higher
      // 2) badge currently unlocking at this level or higher
      const committedLevel = achievementsRef.current[achievementId];
      if (committedLevel && ACHIEVEMENT_LEVEL_RANK[committedLevel] >= ACHIEVEMENT_LEVEL_RANK[level]) {
        return;
      }

      const inFlightLevel = unlockingBadgesRef.current.get(achievementId);
      if (inFlightLevel && ACHIEVEMENT_LEVEL_RANK[inFlightLevel] >= ACHIEVEMENT_LEVEL_RANK[level]) {
        return;
      }

      // DON'T update core state yet - only add to unlocking badges
      // Core state will be updated when animation completes (in onBadgeUnlockComplete)
      onBadgeUnlockStart(achievementId, level);
    },
    [onBadgeUnlockStart],
  );

  const activateAdvancedMode = useCallback(() => {
    setProgress(prev => ({
      ...prev,
      advancedMode: true,
      currentPage: 'practice',
      lastActivityTime: Date.now(),
    }));
  }, []);

  const setCurrentPage = useCallback((page: LearningPage) => {
    setProgress(prev => ({
      ...prev,
      currentPage: page,
      advancedMode: page === 'practice',
      lastActivityTime: Date.now(),
    }));
  }, []);

  const setSelectedGoal = useCallback((achievementId: string) => {
    setProgress(prev => ({
      ...prev,
      selectedGoal: achievementId,
      lastActivityTime: Date.now(),
    }));
  }, []);

  const updateManipulativeState = useCallback((state: Partial<ManipulativeState>) => {
    setProgress(prev => ({
      ...prev,
      manipulativeState: {
        ...prev.manipulativeState,
        ...state,
      },
      lastActivityTime: Date.now(),
    }));
  }, []);

  const recordAdvancedModeAnswer = useCallback((isCorrect: boolean, isFirstTry: boolean) => {
    if (!isCorrect) {
      // Wrong answer breaks the streak (unless it's already at max)
      setProgress(prev => ({
        ...prev,
        advancedModeCurrentStreak: prev.advancedModeCurrentStreak >= 5 ? prev.advancedModeCurrentStreak : 0,
        lastActivityTime: Date.now(),
      }));
      return;
    }

    // Correct answer
    setProgress(prev => {
      const newQuestionsCorrect = prev.advancedModeQuestionsCorrect + 1;
      const newFirstTryCorrect = isFirstTry ? prev.advancedModeFirstTryCorrect + 1 : prev.advancedModeFirstTryCorrect;
      // Once streak reaches 5, it can't decrease (un-loseable)
      const newStreak = isFirstTry
        ? prev.advancedModeCurrentStreak + 1
        : prev.advancedModeCurrentStreak >= 5
        ? prev.advancedModeCurrentStreak
        : 0;
      const newMaxStreak = Math.max(newStreak, prev.advancedModeMaxStreak);

      return {
        ...prev,
        advancedModeQuestionsCorrect: newQuestionsCorrect,
        advancedModeFirstTryCorrect: newFirstTryCorrect,
        advancedModeCurrentStreak: newStreak,
        advancedModeMaxStreak: newMaxStreak,
        lastActivityTime: Date.now(),
      };
    });
  }, []);

  const updateQuestionHistory = useCallback((history: QuestionHistory) => {
    setProgress(prev => ({
      ...prev,
      advancedModeQuestionHistory: history,
      lastActivityTime: Date.now(),
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      progress,
      updateProgress,
      resetExperience,
      incrementAttempts,
      markActivityComplete,
      getElapsedTime,
      unlockAchievement,
      activateAdvancedMode,
      setCurrentPage,
      setSelectedGoal,
      updateManipulativeState,
      recordAdvancedModeAnswer,
      updateQuestionHistory,
      unlockingBadges,
      onBadgeUnlockStart,
      onBadgeUnlockComplete,
    }),
    [
      progress,
      updateProgress,
      resetExperience,
      incrementAttempts,
      markActivityComplete,
      getElapsedTime,
      unlockAchievement,
      activateAdvancedMode,
      setCurrentPage,
      setSelectedGoal,
      updateManipulativeState,
      recordAdvancedModeAnswer,
      updateQuestionHistory,
      unlockingBadges,
      onBadgeUnlockStart,
      onBadgeUnlockComplete,
    ],
  );

  return <MathEducationContext.Provider value={contextValue}>{children}</MathEducationContext.Provider>;
}

export function useMathEducation() {
  const context = useContext(MathEducationContext);
  if (!context) {
    throw new Error('useMathEducation must be used within a MathEducationProvider');
  }
  return context;
}
